/**
 * Simplified Performance Types
 * Leverages SWR for cache management and existing isFilterChanged for staleness
 */

// ============== State Machine ==============
export enum HydrationState {
  INIT = 'INIT',
  LOADING_CACHE = 'LOADING_CACHE',
  CACHE_LOADED = 'CACHE_LOADED',
  FETCHING_API = 'FETCHING_API',
  API_COMPLETE = 'API_COMPLETE',
  COMPLETE = 'COMPLETE',
  ERROR = 'ERROR'
}

// ============== Performance Metrics ==============
export interface FilterPerformanceMetrics {
  // Core timing metrics
  t1_ms: number                      // Time to first usable render (from cache)
  t2_ms: number                      // Time to final correct render (after API)
  loadTime_ms: number                // User-perceived load time (t2 if stale, t1 if fresh)

  // Cache effectiveness (from SWR and isFilterChanged)
  cache_hit: boolean                 // Whether cache was used for initial render
  data_was_stale: boolean            // Whether cached data was outdated (from isFilterChanged)

  // Data info
  total_filters_count: number        // Total number of filters

  // Session metadata
  session_id: string
  timestamp: number
  user_id: string | null
  page_path: string
}

// ============== Performance Metrics V1 (with API-specific tracking) ==============
export interface FilterPerformanceMetricsV1 extends FilterPerformanceMetrics {
  // API categorization
  api_type: 'new_api' | 'old_api'    // Which API was used

  // API-specific T2 metrics (actual API fetch time)
  // Note: T1 (cache) is general/shared - use t1_ms from base interface
  new_api_t2_ms: number | null       // API fetch time for new API (chainFiltersV1)
  old_api_t2_ms: number | null       // API fetch time for old API (getAllFiltersData + getUserAssignedBNamesData)

  // User categorization
  is_internal_user: boolean

  // Firebase config state
  internal_enabled: boolean
  external_enabled: boolean

  // Extended user info
  user_email: string | null
  user_org: string | null
  user_name: string | null
  access_level: string | null
}

// ============== Utility Types ==============
export type MetricsReporter = (metrics: FilterPerformanceMetrics) => void | Promise<void>